// ApplicationTypeService.ts
import { axiosRequest } from "./AxiosService";
import { ApplicationTypeListResponse, ApplicationTypeResponse, ThemeOption } from "../type/response/ApplicationTypeResponse";
import { ApplicationType } from "../pages/ApplicationTypeManagement/data/ApplicationType";

/**
 * 申請種別一覧を取得
 * @returns { applicationTypes, availableThemes }
 */
export const getApplicationTypes = async (): Promise<{
  applicationTypes: ApplicationType[],
  availableThemes: ApplicationType['theme'][]
}> => {
  try {
    const response = await axiosRequest<ApplicationTypeListResponse>(
      "/applicationTypes",
      "GET"
    );

    // 取得したデータのフォーマット化
    const formattedApplicationTypes: ApplicationType[] = response.data.map((item: ApplicationTypeResponse) => {
      const formattedType: ApplicationType = {
        id: item.id,
        typeName: item.typeName,
        description: item.description,
        theme: {
          themeId: item.theme.themeId,
          themeName: item.theme.themeName,
          iconName: item.theme.iconName,
          iconColor: item.theme.iconColor,
          iconBgColor: item.theme.iconBgColor,
          iconBdColor: item.theme.iconBdColor,
          buttonColor: item.theme.buttonColor,
          fontColor: item.theme.fontColor,
          buttonBdColor: item.theme.buttonBdColor,
          themeDescription: item.theme.themeDescription,
        },
      };
      return formattedType;
    });

    // availableThemesのフォーマット化
    const formattedThemes: ApplicationType['theme'][] = response.availableThemes.map((theme: ThemeOption) => ({
      themeId: theme.themeId,
      themeName: theme.themeName,
      iconName: theme.iconName,
      iconColor: theme.iconColor,
      iconBgColor: theme.iconBgColor,
      iconBdColor: theme.iconBdColor,
      buttonColor: theme.buttonColor,
      fontColor: theme.fontColor,
      buttonBdColor: theme.buttonBdColor,
      themeDescription: theme.themeDescription,
    }));

    return {
      applicationTypes: formattedApplicationTypes,
      availableThemes: formattedThemes,
    };

  } catch (error) {
    console.error("申請種別一覧の取得に失敗しました:", error);
    return {
      applicationTypes: [],
      availableThemes: [],
    };
  }
};

/**
 * 申請種別新規作成
 * @param typeName 種別名
 * @param description 説明
 * @param themeId テーマID
 */
export const createApplicationType = async (
  typeName: string,
  description: string | undefined,
  themeId: string
): Promise<boolean> => {
  try {
    const response = await axiosRequest<boolean>(
      "/createApplicationType",
      "POST",
      {
        data: {
          typeName,
          description,
          themeId,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("申請種別の新規作成に失敗しました:", error);
    return false;
  }
};

/**
 * 申請種別更新
 * @param id 申請種別ID
 * @param typeName 種別名
 * @param description 説明
 * @param themeId テーマID
 */
export const updateApplicationType = async (
  id: string,
  typeName: string,
  description: string | undefined,
  themeId: string
): Promise<boolean> => {
  try {
    const response = await axiosRequest<boolean>(
      "/updateApplicationType",
      "POST",
      {
        data: {
          id,
          typeName,
          description,
          themeId,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("申請種別の更新に失敗しました:", error);
    return false;
  }
};

/**
 * 申請種別削除
 * @param id 申請種別ID
 */
export const deleteApplicationType = async (id: string): Promise<boolean> => {
  try {
    const response = await axiosRequest<boolean>(
      "/deleteApplicationType",
      "DELETE",
      {
        data: {
          id,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("申請種別の削除に失敗しました:", error);
    return false;
  }
};
